import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  DownloadCloud, Sparkles, CheckCircle2, Loader2,
  AlertCircle, FolderUp, CheckSquare, Square, Layers, RefreshCw, X
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function MessageHubBulkImportWizard() {
  const qc = useQueryClient();
  const [urlsInput, setUrlsInput] = useState("");
  const [phase, setPhase] = useState("input"); // input | previewing | preview | publishing | done
  const [previewResult, setPreviewResult] = useState(null);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [publishProgress, setPublishProgress] = useState(0);
  const [publishSummary, setPublishSummary] = useState(null);

  // Normalize URL lines count
  const parsedUrls = useMemo(() => {
    return urlsInput
      .split("\n")
      .map((l) => l.strip ? l.strip() : l.trim())
      .filter((l) => l && !l.startsWith("#"));
  }, [urlsInput]);

  // Bulk Preview Mutation
  const previewMut = useMutation({
    mutationFn: async () => {
      const res = await api.post("/admin/messagehub/bulk/preview", { urls: parsedUrls });
      return res.data;
    },
    onMutate: () => {
      setPhase("previewing");
    },
    onSuccess: (data) => {
      setPreviewResult(data);
      setPhase("preview");
      // Auto-select all READY items
      const readyItems = (data.items || []).filter((i) => i.status === "READY").map((i) => i.index);
      setSelectedIndices(readyItems);
    },
    onError: (e) => {
      setPhase("input");
      toast.error(formatApiErrorDetail(e) || "Failed to generate Message Hub bulk preview");
    },
  });

  // Bulk Publish Mutation
  const publishMut = useMutation({
    mutationFn: async (selectedItems) => {
      const res = await api.post("/admin/messagehub/bulk/publish", { items: selectedItems });
      return res.data;
    },
    onMutate: () => {
      setPhase("publishing");
      setPublishProgress(0);
    },
    onSuccess: (data) => {
      setPublishSummary(data);
      setPhase("done");
      toast.success(`Successfully published ${data.total_published} Message Hub sermons!`);
      qc.invalidateQueries({ queryKey: ["sermons"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
    onError: (e) => {
      setPhase("preview");
      toast.error(formatApiErrorDetail(e) || "Failed to publish selected Message Hub sermons");
    },
  });

  const handleSelectAllReady = () => {
    if (!previewResult) return;
    const readyItems = previewResult.items.filter((i) => i.status === "READY").map((i) => i.index);
    setSelectedIndices(readyItems);
  };

  const handleUnselectAll = () => {
    setSelectedIndices([]);
  };

  const toggleSelectIndex = (idx) => {
    setSelectedIndices((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const handleExecutePublish = () => {
    if (!selectedIndices.length) {
      toast.error("Please select at least one READY sermon to publish.");
      return;
    }
    const selectedItems = previewResult.items
      .filter((i) => selectedIndices.includes(i.index))
      .map((i) => ({
        title: i.title,
        sermon_code: i.sermon_code,
        speaker: i.speaker || "William Marrion Branham",
        date: i.date,
        year: i.year || "1964",
        location: i.location,
        series: i.series || "General",
        language: i.language || "te",
        description: i.description,
        source_url: i.normalized_url,
        transcript: i.transcript,
        transcripts: i.transcripts,
        status: "published",
      }));

    publishMut.mutate(selectedItems);
  };

  return (
    <div className="space-y-8" data-testid="messagehub-bulk-import-wizard">
      {/* Step 1: Multi-URL Textarea Input */}
      {phase === "input" && (
        <div className="rounded-2xl border hairline bg-card p-6 lg:p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-serif text-xl text-foreground">Message Hub (Telugu) Bulk Import</h2>
              <p className="text-xs text-muted-foreground">
                Paste up to 100+ Message Hub sermon URLs (one URL per line).
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Sermon URLs ({parsedUrls.length} valid lines detected)
            </label>
            <textarea
              rows={8}
              value={urlsInput}
              onChange={(e) => setUrlsInput(e.target.value)}
              placeholder={`https://search.messagehub.info/app/te/sermons#55-0227A\nhttps://search.messagehub.info/app/te/sermons#55-0301\nhttps://search.messagehub.info/app/te/sermons#55-0403`}
              className="w-full rounded-xl border hairline bg-surface-2/40 p-4 font-mono text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              disabled={!parsedUrls.length || previewMut.isPending}
              onClick={() => previewMut.mutate()}
              className="flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-glow hover:opacity-90 disabled:opacity-50"
            >
              {previewMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderUp className="h-4 w-4" />}
              Validate & Preview ({parsedUrls.length} URLs)
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {phase === "previewing" && (
        <div className="rounded-2xl border hairline bg-card p-12 text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <h3 className="font-serif text-lg text-foreground">Extracting Message Hub Metadata & Verbatim Transcripts…</h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">
            Processing {parsedUrls.length} sermon URLs concurrently (5 requests at a time). Please wait…
          </p>
        </div>
      )}

      {/* Step 2: Itemized Preview Table */}
      {(phase === "preview" || phase === "publishing") && previewResult && (
        <div className="space-y-6">
          {/* Summary Bar */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            <div className="rounded-xl border hairline bg-card p-4 text-center">
              <div className="text-xs text-muted-foreground uppercase">Submitted</div>
              <div className="text-xl font-bold text-foreground mt-1">{previewResult.total_submitted}</div>
            </div>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
              <div className="text-xs text-emerald-400 font-semibold uppercase">Ready</div>
              <div className="text-xl font-bold text-emerald-400 mt-1">{previewResult.total_ready}</div>
            </div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-center">
              <div className="text-xs text-amber-400 font-semibold uppercase">Duplicates</div>
              <div className="text-xl font-bold text-amber-400 mt-1">{previewResult.total_duplicates}</div>
            </div>
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-center">
              <div className="text-xs text-rose-400 font-semibold uppercase">Failed</div>
              <div className="text-xl font-bold text-rose-400 mt-1">{previewResult.total_failed}</div>
            </div>
            <div className="rounded-xl border border-zinc-500/30 bg-zinc-500/10 p-4 text-center">
              <div className="text-xs text-zinc-400 font-semibold uppercase">Invalid</div>
              <div className="text-xl font-bold text-zinc-400 mt-1">{previewResult.total_invalid}</div>
            </div>
          </div>

          {/* Table Controls */}
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border hairline bg-card p-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSelectAllReady}
                className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
              >
                <CheckSquare className="h-4 w-4" /> Select All Ready ({previewResult.total_ready})
              </button>
              <span className="text-muted-foreground/30">|</span>
              <button
                type="button"
                onClick={handleUnselectAll}
                className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <Square className="h-4 w-4" /> Unselect All
              </button>
              <span className="text-xs text-muted-foreground ml-2">
                Selected: <strong className="text-foreground">{selectedIndices.length}</strong> sermons
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPhase("input")}
                className="rounded-full border hairline bg-surface-2/60 px-4 py-2 text-xs text-foreground hover:bg-surface-2"
              >
                Edit URLs
              </button>
              <button
                disabled={!selectedIndices.length || phase === "publishing"}
                onClick={handleExecutePublish}
                className="flex items-center gap-2 rounded-full bg-primary px-6 py-2 text-xs font-medium text-primary-foreground shadow-glow hover:opacity-90 disabled:opacity-50"
              >
                {phase === "publishing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderUp className="h-4 w-4" />}
                Publish Selected ({selectedIndices.length})
              </button>
            </div>
          </div>

          {/* Results Table */}
          <div className="overflow-x-auto rounded-xl border hairline bg-card">
            <table className="w-full text-left text-xs">
              <thead className="border-b hairline bg-surface-2/40 text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="p-3 w-10 text-center">Sel</th>
                  <th className="p-3 w-12 text-center">#</th>
                  <th className="p-3 w-28">Status</th>
                  <th className="p-3 w-28">Code</th>
                  <th className="p-3">Title</th>
                  <th className="p-3 w-24">Date</th>
                  <th className="p-3 w-28">Passages</th>
                  <th className="p-3">Error / Note</th>
                </tr>
              </thead>
              <tbody className="divide-y hairline">
                {previewResult.items.map((item) => {
                  const isChecked = selectedIndices.includes(item.index);
                  return (
                    <tr key={item.index} className={`hover:bg-surface-2/30 ${isChecked ? "bg-primary/5" : ""}`}>
                      <td className="p-3 text-center">
                        <input
                          type="checkbox"
                          disabled={item.status !== "READY" && item.status !== "DUPLICATE"}
                          checked={isChecked}
                          onChange={() => toggleSelectIndex(item.index)}
                          className="rounded border-zinc-700 bg-zinc-900 text-primary focus:ring-primary"
                        />
                      </td>
                      <td className="p-3 text-center font-mono text-muted-foreground">{item.index}</td>
                      <td className="p-3 font-semibold">
                        {item.status === "READY" && (
                          <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-400 ring-1 ring-emerald-500/20">
                            READY
                          </span>
                        )}
                        {item.status === "DUPLICATE" && (
                          <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-400 ring-1 ring-amber-500/20">
                            DUPLICATE
                          </span>
                        )}
                        {item.status === "FAILED" && (
                          <span className="inline-flex items-center rounded-full bg-rose-500/10 px-2 py-0.5 text-[11px] text-rose-400 ring-1 ring-rose-500/20">
                            FAILED
                          </span>
                        )}
                        {item.status === "INVALID" && (
                          <span className="inline-flex items-center rounded-full bg-zinc-500/10 px-2 py-0.5 text-[11px] text-zinc-400 ring-1 ring-zinc-500/20">
                            INVALID
                          </span>
                        )}
                      </td>
                      <td className="p-3 font-mono font-medium text-foreground">{item.sermon_code || "—"}</td>
                      <td className="p-3 font-medium text-foreground leading-snug">{item.title || item.raw_url}</td>
                      <td className="p-3 text-muted-foreground">{item.date || item.year || "—"}</td>
                      <td className="p-3 text-muted-foreground">
                        {item.paragraph_count ? `${item.paragraph_count} passages` : "—"}
                      </td>
                      <td className="p-3 text-muted-foreground max-w-xs truncate">{item.error || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step 3: Complete Summary Screen */}
      {phase === "done" && publishSummary && (
        <div className="rounded-2xl border hairline bg-card p-8 space-y-6 text-center">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <div>
            <h2 className="font-serif text-2xl text-foreground">Bulk Import Completed!</h2>
            <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
              Successfully processed and stored Message Hub Telugu sermons in your library.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto sm:grid-cols-3">
            <div className="rounded-xl border hairline bg-surface-2/40 p-4">
              <div className="text-xs text-muted-foreground">Total Selected</div>
              <div className="text-xl font-bold text-foreground mt-1">{publishSummary.total_submitted}</div>
            </div>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
              <div className="text-xs text-emerald-400 font-semibold">Published</div>
              <div className="text-xl font-bold text-emerald-400 mt-1">{publishSummary.total_published}</div>
            </div>
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
              <div className="text-xs text-rose-400 font-semibold">Failed</div>
              <div className="text-xl font-bold text-rose-400 mt-1">{publishSummary.total_failed}</div>
            </div>
          </div>

          <div className="flex justify-center gap-3 pt-4">
            <button
              onClick={() => {
                setUrlsInput("");
                setPreviewResult(null);
                setSelectedIndices([]);
                setPublishSummary(null);
                setPhase("input");
              }}
              className="rounded-full bg-primary px-6 py-2.5 text-xs font-medium text-primary-foreground shadow-glow hover:opacity-90"
            >
              Start Another Bulk Import
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
